import { useState, useEffect, useRef } from 'react';
import AppHeader from '@/components/AppHeader';
import { useNavigate } from 'react-router-dom';
import { getClubSettings, setClubSetting, uploadLogo } from '@/lib/supabase';

interface ClubSettingsProps {
  onSignOut: () => void;
}

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/jpg,image/webp';
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

export default function ClubSettings({ onSignOut }: ClubSettingsProps) {
  const navigate = useNavigate();
  const [clubName, setClubName] = useState('Starwood Sports Club');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getClubSettings()
      .then(settings => {
        setClubName(settings.club_name ?? 'Starwood Sports Club');
        setLogoUrl(settings.club_logo_url ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_BYTES) {
      setError('Logo file must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
    setError(null);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await setClubSetting('club_name', clubName.trim() || 'Starwood Sports Club');

      if (removeLogo && !logoFile) {
        await setClubSetting('club_logo_url', null);
        setLogoUrl(null);
      }

      if (logoFile) {
        const url = await uploadLogo('club-logos', logoFile, `club-logo-${Date.now()}`);
        await setClubSetting('club_logo_url', url);
        setLogoUrl(url);
        setLogoFile(null);
        setLogoPreview(null);
      }

      setRemoveLogo(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch {
      setError('Failed to save club settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const currentLogoSrc = logoPreview ?? (removeLogo ? null : logoUrl);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title="Club Branding"
        navIcon="back"
        onNavigate={() => navigate('/admin')}
      />

      <div className="flex-1 overflow-y-auto px-4 py-6 safe-area-bottom max-w-md mx-auto w-full space-y-4">

        {/* Club Logo */}
        <div className="card">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Club Logo</h2>

          <div className="flex items-center gap-4 mb-4">
            {currentLogoSrc ? (
              <div className="relative flex-shrink-0">
                <img
                  src={currentLogoSrc}
                  alt="Club logo"
                  className="w-20 h-20 rounded-xl object-contain border border-border bg-surface-raised"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-error rounded-full flex items-center justify-center text-white text-sm font-bold leading-none"
                  aria-label="Remove logo"
                >×</button>
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center flex-shrink-0 cursor-pointer hover:border-text-secondary transition-colors"
                onClick={() => logoInputRef.current?.click()}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted mb-1">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span className="text-text-muted text-xs">No logo</span>
              </div>
            )}

            <div className="flex-1">
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="w-full py-2.5 px-3 text-sm font-semibold text-text-primary border border-border rounded-lg hover:border-text-secondary transition-colors text-left mb-1"
              >
                {currentLogoSrc ? 'Replace Logo' : 'Upload Logo'}
              </button>
              {currentLogoSrc && (
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="w-full py-2 px-3 text-sm font-semibold text-error border border-error/40 rounded-lg hover:border-error transition-colors text-left"
                >
                  Remove Logo
                </button>
              )}
              <p className="text-xs text-text-muted mt-2">PNG, JPG, WEBP · max 5 MB</p>
            </div>
          </div>

          <input ref={logoInputRef} type="file" accept={ACCEPTED_TYPES} onChange={handleLogoFileChange} className="hidden" />
        </div>

        {/* Club Name */}
        <div className="card">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Club Name</h2>
          <input
            type="text"
            value={clubName}
            onChange={e => setClubName(e.target.value)}
            className="input-field"
            placeholder="e.g. Starwood Sports Club"
            maxLength={80}
          />
          <p className="text-xs text-text-muted mt-1">Displayed on the login screen and tournament headers.</p>
        </div>

        {/* Preview */}
        {(currentLogoSrc || clubName) && (
          <div className="card bg-surface-raised">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Preview</h2>
            <div className="flex flex-col items-center gap-2 py-2">
              {currentLogoSrc && (
                <img src={currentLogoSrc} alt="Club logo preview" className="w-16 h-16 object-contain rounded-lg" />
              )}
              <p className="text-base font-bold text-text-primary text-center">{clubName || 'Starwood Sports Club'}</p>
              <p className="text-xs text-text-muted">Volleyball Tournament Scoring System</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error/30 rounded-lg px-4 py-3">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success/30 rounded-lg px-4 py-3">
            <p className="text-success text-sm font-semibold">Club settings saved.</p>
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary bg-serving text-white font-bold disabled:opacity-50"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              SAVING...
            </span>
          ) : 'SAVE SETTINGS'}
        </button>
      </div>
    </div>
  );
}
